<template>
  <section class="container-fluid" data-testid="paypal-vault-card-form">
    <div class="row">
      <div class="col-xs-12">
        <form @submit.prevent="submit()" novalidate>
          <fieldset>
            <h3 class="mb-4">Card Information</h3>

            <!-- PayPal Card Fields for Vault -->
            <div class="form-group">
              <label for="vault_card_name_field">Name on Card</label>
              <section id="vault_card_name_field"></section>
            </div>

            <div class="form-group">
              <label for="vault_card_number_field">Card Number</label>
              <section id="vault_card_number_field"></section>
            </div>

            <div class="form-group">
              <label for="vault_card_expiry_field">Expiration Date</label>
              <section id="vault_card_expiry_field"></section>
            </div>

            <div class="form-group">
              <label for="vault_card_cvv_field">CVC</label>
              <section id="vault_card_cvv_field"></section>
            </div>
          </fieldset>

          <div class="text-center" style="margin-bottom: 2em">
            <button
              class="btn btn-dc"
              :disabled="submitting || !cardFieldsReady"
              style="margin-top: 2em"
            >
              {{ submitting ? 'Saving...' : 'Save Payment Method' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import { loadScript } from "@paypal/paypal-js";

export default {
  props: {
    clientId: { type: String, required: true },
    studentId: { type: Number, required: true },
    billingAddress: {
      type: Object,
      default: function () {
        return {
          street: "",
          state: "",
          city: "",
          zip: "",
          country: "US",
        };
      },
    },
  },
  data() {
    return {
      paypal: null,
      paypalLoaded: false,
      cardFields: null,
      nameField: null,
      numberField: null,
      expiryField: null,
      cvvField: null,
      cardFieldsReady: false,
      submitting: false,
    };
  },
  async mounted() {
    try {
      this.paypal = await loadScript({
        "client-id": this.clientId,
        components: ["card-fields"],
      });
      this.setupPayPal();
    } catch (error) {
      console.error("Failed to load PayPal SDK:", error);
      this.$emit("error", error);
    }
  },
  beforeDestroy() {
    // Clean up PayPal card fields to prevent scroll issues after navigation
    // Each field creates an iframe that must be explicitly closed
    if (this.nameField) {
      this.nameField.close();
    }
    if (this.numberField) {
      this.numberField.close();
    }
    if (this.expiryField) {
      this.expiryField.close();
    }
    if (this.cvvField) {
      this.cvvField.close();
    }
  },
  methods: {
    setupPayPal() {
      if (!this.paypal || !this.paypal.CardFields) {
        console.error("PayPal Card Fields not available");
        return;
      }

      this.paypalLoaded = true;

      // Create the card fields component for vault flow
      this.cardFields = this.paypal.CardFields({
        style: {
          input: {
            "font-size": "16px",
            "font-family": "helvetica, tahoma, calibri, sans-serif",
            color: "#3a3a3a",
          },
          ":focus": {
            color: "black",
          },
          ".invalid": {
            color: "red",
          },
        },
        // Use createVaultSetupToken for saving cards without purchase
        createVaultSetupToken: async () => {
          // Call backend to get a setup token
          const response = await this.getSetupToken();
          return response.setup_token_id;
        },
        onApprove: async (data) => {
          try {
            // Exchange the setup token for a payment token
            await this.savePaymentMethod(data.vaultSetupToken);
            this.$emit("saved");
          } catch (error) {
            console.error("Failed to save payment method:", error);
            this.$emit("error", error);
          } finally {
            this.submitting = false;
          }
        },
        onError: (err) => {
          console.error("PayPal Card Fields error:", err);
          this.submitting = false;
          this.$emit("error", err);
        },
        inputEvents: {
          onChange: (data) => {
            // Track field validity if needed
          },
        },
      });

      // Check if card fields are eligible
      if (this.cardFields.isEligible()) {
        // Render individual fields
        this.nameField = this.cardFields.NameField();
        this.nameField.render("#vault_card_name_field");

        this.numberField = this.cardFields.NumberField();
        this.numberField.render("#vault_card_number_field");

        this.expiryField = this.cardFields.ExpiryField();
        this.expiryField.render("#vault_card_expiry_field");

        this.cvvField = this.cardFields.CVVField();
        this.cvvField.render("#vault_card_cvv_field");

        this.cardFieldsReady = true;
      } else {
        console.error("PayPal Card Fields not eligible");
        this.$emit("error", new Error("PayPal Card Fields not eligible for this account"));
      }
    },

    async getSetupToken() {
      // Call the backend to get a PayPal setup token
      const response = await fetch(
        `/api/v1/users/students/${this.studentId}/payment-methods/setup-token`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get setup token");
      }

      return response.json();
    },

    async savePaymentMethod(setupToken) {
      // Exchange the setup token for a payment token via backend
      const response = await fetch(
        `/api/v1/users/students/${this.studentId}/payment-methods`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
          body: JSON.stringify({
            setup_token: setupToken,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save payment method");
      }

      return response.json();
    },

    async submit() {
      if (!this.cardFields) {
        console.error("PayPal Card Fields not initialized");
        return;
      }

      this.submitting = true;

      try {
        // Submit the card fields - this triggers createVaultSetupToken and onApprove
        await this.cardFields.submit();
      } catch (err) {
        console.error("PayPal submission error:", err);
        this.submitting = false;
        this.$emit("error", err);
      }
    },
  },
};
</script>

<style scoped>
/* PayPal Card Fields styling - matches PayPalCard.vue */
#vault_card_name_field,
#vault_card_number_field,
#vault_card_expiry_field,
#vault_card_cvv_field {
  min-height: 40px;
}
</style>
